import { Component, forwardRef, Injector, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';

@Component({
    selector: 'nx-edit-multi-key-value-input',
    template: `
    <div>
        <label for="text">{{label | translate}}<ng-container *ngIf=isRequired()>*</ng-container></label>
        <div
            *ngIf="!values || values.length === 0" 
            class="text-[14px] text-[#888484] ml-2 mt-2">
            {{ emptyMessage | translate }}
        </div>
        <div
            *ngFor="let item of values; let i = index"
            [ngClass]="['flex', 'flex-row', 'gap-2', i !== 0 ? 'mt-2' : '']">
            <input
                [type]="'text'" 
                [value]="item?.key || ''"
                [placeholder]="placeholderKey | translate"
                id="text"
                pInputText
                (input)="onKeyChanged($event, i)"
                [disabled]="isDisabled"
                [ngClass]="{'invalid': ngControl?.invalid && ngControl?.touched}"/>
            <input
                [type]="'text'" 
                [value]="item?.value || ''"
                [placeholder]="placeholderValue | translate"
                (input)="onValueChanged($event, i)"
                id="text"
                pInputText
                [disabled]="isDisabled"
                [ngClass]="{'invalid': ngControl?.invalid && ngControl?.touched}"/>
            <nx-default-button
                class="mt-[2px]"
                [tooltip]="removeEntryTooltip | translate"
                (click)="removeEntry(i)"
                [icon]="'icon-trash'"
                [style]="'light'">
            </nx-default-button>
        </div>
        <div class="mt-2">
            <nx-default-button
                [disabled]="isAddEntryDisabled()"
                [label]="addEntryLabel | translate"
                [icon]="'icon-plus'"
                (click)="addEntry()"
                [style]="'light'">
            </nx-default-button>
        </div>
    </div>
  `,
    styles: [`
        .placeholder{
            font-size: 0.875rem;
            color: #888484;
        }
  `],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EditMultiKeyValueInputComponent),
            multi: true
        }
    ]
})
export class EditMultiKeyValueInputComponent implements ControlValueAccessor, OnInit {

    public values: { key: string, value: string }[] | undefined;

    ngControl: NgControl;
    isDisabled: boolean;
    @Input() label: string;
    @Input() placeholderKey: string = '';
    @Input() placeholderValue: string = '';
    @Input() addEntryLabel: string = 'coreModuleModule.editMultiKeyValueInputComponent.addEntry';
    @Input() removeEntryTooltip: string = 'coreModuleModule.editMultiKeyValueInputComponent.removeEntryTooltip';
    @Input() emptyMessage: string = 'coreModuleModule.editMultiKeyValueInputComponent.emptyMessage';

    private onChange: (value: any) => void;
    private onTouched: () => void;

    constructor(private _injector: Injector) {
    }

    ngOnInit() {
        this.ngControl = this._injector.get(NgControl);
        this.ngControl.valueAccessor = this;
    }

    writeValue(value: any): void {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            this.values = Object.entries(value).map(([key, val]) => ({ key, value: val as string }));
        } else {
            this.values = [];
        }
    }

    registerOnChange(fn: (value: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        // Optional: Implementieren Sie die Logik zum Deaktivieren des Steuerelements
        this.isDisabled = isDisabled;
    }

    onBlur(): void {
        if (this.onTouched) {
            this.onTouched();
        }
    }

    isRequired(): boolean {
        return this.ngControl?.control?.hasValidator(Validators.required);
    }

    public addEntry(): void {
        if (!this.values) {
            this.values = [];
        }
        this.values.push({ key: '', value: '' });
    }

    public removeEntry(index: number): void {
        if (this.values && index >= 0 && index < this.values.length) {
            this.values.splice(index, 1);
            this.propagateValues();
        }
    }

    // add entry is disabled, if the last entry is not filled out. Filled out
    // means that both key and value are not empty.
    // If there are no entries, the button is enabled.
    public isAddEntryDisabled(): boolean {
        if (!this.values || this.values.length === 0) {
            return false;
        }
        // check if the last entry is filled out
        const lastEntry = this.values[this.values.length - 1];
        return !lastEntry.key || !lastEntry.value;
    }

    onValueChanged(event: Event, index: number): void {
        const input = event.target as HTMLInputElement;
        if (this.values && index >= 0 && index < this.values.length) {
            this.values[index].value = input.value;
            this.propagateValues();
        }
    }

    onKeyChanged(event: Event, index: number): void {
        const input = event.target as HTMLInputElement;
        if (this.values && index >= 0 && index < this.values.length) {
            this.values[index].key = input.value;
            this.propagateValues();
        }
    }

    private propagateValues(): void {
        if (this.onChange) {
            const obj = (this.values || [])
                .filter(entry => entry.key) // skip empty keys
                .reduce((acc, entry) => {
                    acc[entry.key] = entry.value;
                    return acc;
                }, {});
            this.onChange(obj);
        }
    
    }

}